import { ColDef, PostSortRowsParams, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aggridHeaderClass, titleMRClass } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, RemoveRowHandler, InsertRowHandler, postSortHandler, useCellChangeHandler, useRowSelectionHandler, createCellCssClassRules, GridCellConfig } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, getRowStyleFlags, IndexParameterCell, onGridReady } from "src/utils/grid/gridStyleUtils";
import { useTQC2050_R01Store } from "./Store_TQC2050_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultTQC2050_R01DetailDto, TQC2050_R01DetailDto } from "./Dto_TQC2050_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { getItemPopupList } from "src/apis/itemApi";
import { ItemDto } from "src/dtos/item/itemDto";
import { showWarning, showSuccess } from "src/utils/toast/toastUtil";
import { getCommonCodeColumnOptions } from "src/utils/grid/GridColumnContent";

// 범용 훅 임포트
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";
import { useDetailTableWithItemSearch } from "src/hooks/detail-table/useDetailTableWithItemSearch";

import { createModuleConfig } from "src/utils/detail-table/detailTableUtils";
import { calculateDetailAmount } from "src/utils/calculation/calculationUtils";
import { useMouseContent } from "src/utils/mouse/mouseContent";
import { useCodeSearch } from "src/hooks/useCodeSearch";

const COLUMN_STATE_KEY = 'TQC2050_R01-detail-grid-column-state';

interface Props {
	detailGridRef: React.RefObject<AgGridReact<TQC2050_R01DetailDto> | null>;
}

export const DetailTable_TQC2050_R01 = (props: Props) => {

	const { detailGridRef } = props;

	const {
		TQC2050_R01DetailList, setTQC2050_R01DetailList,
		updateTQC2050_R01DetailList,
		selectedTQC2050_R01DetailList, setSelectedTQC2050_R01DetailList,
		initSelectedTQC2050_R01DetailList,
		deletedTQC2050_R01DetailList, addDeletedTQC2050_R01DetailList,
		originalTQC2050_R01DetailList,
		editTQC2050_R01,
		detailColumnState, setDetailColumnState,
		detailScrollTop, setDetailScrollTop,
		setPrevSampleCount,
		setIsEditing,
	} = useTQC2050_R01Store();

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef: detailGridRef,
		key: 'valueIndex',
		gridData: TQC2050_R01DetailList,
		defaultGridData: defaultTQC2050_R01DetailDto,
		selectedRow: selectedTQC2050_R01DetailList,
		originalGridData: originalTQC2050_R01DetailList,
		updateRow: (index: number, data: any) => updateTQC2050_R01DetailList(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		testValue: '시험결과',
		remark: '비고',
	};

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: [''],
		insertedDisabledCells: [''],
		commonDisabledCells: [''],
		
		pointerCells: ['testValue'],
		insertedPointerCells: [''],
		commonPointerCells: [''],
		
		alignLeftCells: ['']
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);
	
	const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<TQC2050_R01DetailDto>({
            idKey: 'valueIndex',
            selectedList: selectedTQC2050_R01DetailList,
            deletedList: deletedTQC2050_R01DetailList,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
        selectedTQC2050_R01DetailList, deletedTQC2050_R01DetailList,
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns, createCellClassRules,
	]);

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = ['testValue', 'remark'];
	
	// 품목 검색 함수
	const { searchExactCode } = useCodeSearch({});

	const baseColumnDefs: ColDef<TQC2050_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '시험결과', colId: 'testValue', field: 'testValue', width: 200,
			editable: true,
			pasteable: true,
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
	], []);

	
	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<TQC2050_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<TQC2050_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [detailColumnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleCellChange = useCellChangeHandler({
		dataList: TQC2050_R01DetailList,
		originalDataList: originalTQC2050_R01DetailList,
		updateRow: (index: number, data: TQC2050_R01DetailDto) => {
			//  입력 중 플래그 설정 (Zustand 스토어 사용)
			setIsEditing(true);
			
			//  TQC2050_R01DetailList만 업데이트 (단일 소스 원칙)
			updateTQC2050_R01DetailList(index, data);
			
			//  입력 완료 후 플래그 해제 (약간의 지연을 두어 sampleCount 동기화가 실행되지 않도록)
			setTimeout(() => {
				setIsEditing(false);
			}, 100);
		},
		customBeforeUpdate: (updatedData, field, value) => {
			return updatedData;
		},
	});

	const handleRowClick = useCallback((e: RowClickedEvent<TQC2050_R01DetailDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		const isSelected = selectedTQC2050_R01DetailList.includes(rowData['valueIndex'] as any);

		if (isSelected) {
			setSelectedTQC2050_R01DetailList([]);
		} else {
			setSelectedTQC2050_R01DetailList([rowData['valueIndex']] as any);
		}
	}, [selectedTQC2050_R01DetailList, setSelectedTQC2050_R01DetailList]);

	const commonInputColumns = ['testValue', 'remark'];
	const cellClickActions: CustomCellClickAction<TQC2050_R01DetailDto>[] = useMemo(() => [
		{
			validate: (field, data) => {
				return commonInputColumns.includes(field);
			},
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], []);

	const handleCellClick = CreateCellClickHandler(TQC2050_R01DetailList, 'valueIndex', cellClickActions);




	// 공통 상세 테이블 핸들러 - 간단한 설정으로 변경
	const detailTableHandlers = {
		settingCodeSearchModalProps: (field: string, data: any) => {}
	};

	const cellEventHandlers = useDetailTableWithItemSearch<TQC2050_R01DetailDto>({
		detailList: TQC2050_R01DetailList,
		editableColumns: defaultEditableColumns,
		itemSearchConfig: {
			config: {
				category: '품목',
			responseMapping: {
				itemNm: 'testValue',
			},
			clearFields: {
				testValue: "", remark: ""
			},
				searchFunction: (code: string, onSuccess?: (item: any) => void) => 
					searchExactCode('item', code, onSuccess)
			},
			settingCodeSearchModalProps: detailTableHandlers.settingCodeSearchModalProps,
		},
		keyDownConfig: {
			qtyField: 'testValue',
			enableArrowNavigation: true
		}
	});

	const cellDoubleClickActions: CustomCellDoubleClickAction<TQC2050_R01DetailDto>[] = useMemo(() => [
		{
			validate: (field, data) => field === 'testValue',
			action: (e, foundData) => {
				// 더블클릭 시 편집 모드 시작
				e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! });
			}
		},
	], []);

	const handleCellDoubleClick = CreateCellDoubleClickHandler(TQC2050_R01DetailList, 'valueIndex', cellDoubleClickActions);

	const handlePostSort = useCallback((params: PostSortRowsParams<TQC2050_R01DetailDto>) => {
		postSortHandler(params, TQC2050_R01DetailList, originalTQC2050_R01DetailList, setTQC2050_R01DetailList);
	}, [TQC2050_R01DetailList, originalTQC2050_R01DetailList, setTQC2050_R01DetailList]);

	const pinnedBottomRowData = useMemo(() => {
		const totalCount = TQC2050_R01DetailList
			.reduce((sum, item) => sum + (deletedTQC2050_R01DetailList.includes(item.valueIndex.toString()) ? 0 : 1), 0);

		return [{
			testValue: `${TQC2050_R01DetailList.length.toLocaleString()}건`,
		}];
	}, [TQC2050_R01DetailList, deletedTQC2050_R01DetailList]);

	//  1단계: testDetails 동기화 useEffect 제거
	// TQC2050_R01DetailList를 단일 소스로 사용하므로 testDetails 동기화 로직 불필요
	// API 응답에서 testDetails가 오는 경우는 Content_TQC2050_R01.tsx의 fetchOne에서 처리

	// 시료수(sampleCount) 변경 시 DetailTable 행 수 동기화
	useEffect(() => {
		if (!editTQC2050_R01 || editTQC2050_R01.sampleCount === undefined || editTQC2050_R01.sampleCount === null) {
			return;
		}

		const targetCount = Number(editTQC2050_R01.sampleCount);
		
		//  Zustand 스토어에서 최신 값 직접 참조 (의존성 배열에 포함하지 않아도 최신 값 참조 가능)
		const storeState = useTQC2050_R01Store.getState();
		const currentPrevSampleCount = storeState.prevSampleCount;
		const currentIsEditing = storeState.isEditing;
		
		//  등록 버튼 클릭 시 즉시 실행되도록 보장: 신규 등록 상태일 때 prevSampleCount 초기화
		const isNewRecord = !editTQC2050_R01.testResultNo || editTQC2050_R01.testResultNo === '';
		
		if (isNewRecord) {
			// 신규 등록 상태이면 prevSampleCount 초기화하여 즉시 실행 보장
			if (currentPrevSampleCount !== undefined) {
				setPrevSampleCount(undefined);
			}
		}
		
		//  이전 값과 같으면 실행하지 않음 (무한 루프 방지)
		if (currentPrevSampleCount === targetCount) {
			return;
		}

		//  입력 중일 때는 sampleCount 동기화 건너뛰기 (하지만 prevSampleCount는 업데이트)
		if (currentIsEditing) {
			if (currentPrevSampleCount !== targetCount) {
				setPrevSampleCount(targetCount);
			}
			return;
		}

		if (targetCount <= 0) {
			// 시료수가 0 이하면 빈 리스트로 설정
			setTQC2050_R01DetailList([]);
			setPrevSampleCount(targetCount);
			return;
		}

		// 삭제된 행을 제외한 활성 행 수 계산
		const deletedSet = new Set(deletedTQC2050_R01DetailList.map(v => String(v)));
		const activeDetails = TQC2050_R01DetailList.filter(d => !deletedSet.has(String(d.valueIndex)));
		const currentActiveCount = activeDetails.length;

		// 시료수와 현재 활성 행 수가 다르면 조정
		if (currentActiveCount !== targetCount) {
			if (currentActiveCount < targetCount) {
				// 부족한 만큼 신규행 추가
				//  기존 저장된 행(activeDetails)은 그대로 유지하고, 부족한 만큼만 신규행 추가
				const needToAdd = targetCount - currentActiveCount;
				const newRows: TQC2050_R01DetailDto[] = [];
				
				// 최대 valueIndex 계산 (기존 행의 valueIndex를 기준으로 새 행의 valueIndex 생성)
				const maxValueIndex = TQC2050_R01DetailList.length > 0 
					? Math.max(...TQC2050_R01DetailList.map(d => d.valueIndex || 0))
					: 0;
				
				for (let i = 0; i < needToAdd; i++) {
					const newRow: TQC2050_R01DetailDto = {
						...defaultTQC2050_R01DetailDto,
						valueIndex: maxValueIndex + i + 1,
						orderIndex: activeDetails.length + i + 1,
						orderIndexSeq: 0,
						inserted: true, // 신규 행 표시
						isModified: false,
					};
					newRows.push(newRow);
				}
				
				//  기존 활성 행들(저장된 데이터 포함) + 새로 추가할 행들
				// 기존 행은 inserted: false로 유지되고, 새 행만 inserted: true로 추가됨
				const updatedList = [...activeDetails, ...newRows];
				setTQC2050_R01DetailList(updatedList);
			} else if (currentActiveCount > targetCount) {
				// 초과한 만큼 뒤에서부터 제거 (삭제 표시)
				const needToRemove = currentActiveCount - targetCount;
				const rowsToRemove = activeDetails.slice(-needToRemove);
				const valueIndexesToRemove = rowsToRemove
					.filter(row => row.inserted) // 신규로 추가된 행만 삭제
					.map(row => String(row.valueIndex));
				
				if (valueIndexesToRemove.length > 0) {
					// 신규 행이면 완전히 제거
					const remainingRows = activeDetails.filter(
						row => !valueIndexesToRemove.includes(String(row.valueIndex))
					);
					setTQC2050_R01DetailList(remainingRows);
				} else {
					// 기존 행이면 삭제 표시만
					const rowsToDelete = rowsToRemove.map(row => String(row.valueIndex));
					addDeletedTQC2050_R01DetailList(rowsToDelete);
				}
			}
		}

		//  이전 값 업데이트 (Zustand 스토어 사용)
		// 이전 값과 다를 때만 업데이트하여 무한 루프 방지
		const finalStoreState = useTQC2050_R01Store.getState();
		if (finalStoreState.prevSampleCount !== targetCount) {
			setPrevSampleCount(targetCount);
		}
	}, [editTQC2050_R01?.sampleCount, editTQC2050_R01?.testResultNo]);

	return (
		<div className="w-full h-full">
		
			<GridContextWrapper<TQC2050_R01DetailDto>
				enableCellCalculation
				gridWrapperRef={gridWrapperRef}
				gridRef={detailGridRef}
				paddedGridData={paddedGridData}
				setPaddedGridData={setTQC2050_R01DetailList}
				initSelected={initSelectedTQC2050_R01DetailList}
				defaultRowData={defaultTQC2050_R01DetailDto}
				baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
				defaultColumnState={defaultColumnState}	
				setColumnState={setDetailColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				menuItems={undefined}
			>
				<Grid_Excel<TQC2050_R01DetailDto>
					ref={detailGridRef}
					rowData={paddedGridData}
					getRowId={getRowId}
					columnDefs={columnDefs}
					defaultColDef={cellCssDefaultColDef}
					
					rowClassRules={createRowClassRules<TQC2050_R01DetailDto>({
						idKey: 'valueIndex',
						selectedList: selectedTQC2050_R01DetailList,
						deletedList: deletedTQC2050_R01DetailList
					})}
					onRowClicked={handleRowClick}
					onCellClicked={handleCellClick}
					onCellKeyDown={cellEventHandlers.handleCellKeyDown}
					onCellValueChanged={handleCellChange}
					pinnedBottomRowData={pinnedBottomRowData}
					onPostSortRows={handlePostSort}
					columnStateKey={COLUMN_STATE_KEY}
					setColumnState={setDetailColumnState}
					getDefaultColumnState={() => defaultColumnState}
					scrollTop={detailScrollTop}
					setScrollTop={setDetailScrollTop}
				/>
			</GridContextWrapper>
		</div>
	)
}

export default DetailTable_TQC2050_R01;

